import React, { Component } from "react";
import { Modal, Form, Input, notification } from "antd";
import Axios from "axios";
import moment from "moment";
import { Link } from "react-router-dom";
import {
  Save_Mother_Parameter_Data,
  LOADER,
} from "../../../utils/constant";
import { speechService, speechTextService } from "../../../utils/rxjs-sharing";
import LS_SERVICE from "../../../utils/localStorage";

/**
 * MotherParameterModal
 *
 * Self-contained modal that hosts all mother-parameter fields with its own antd
 * Form, state, handlers and a Save button. Each save inserts a NEW mother-parameter
 * record for the current reference id (no id is sent), which lets a doctor record
 * multiple parameter readings for the same visit. The ANC card shows the latest one.
 *
 * Props:
 * - visible (bool)        : controls modal visibility
 * - onClose ()            : called when the modal is closed/cancelled
 * - allowEdit (bool)      : whether fields are editable (default true)
 * - onSaved () (optional) : called after a successful save (to refresh the display)
 */
class MotherParameterModal extends Component {
  constructor(props) {
    super(props);
    this.formRef = React.createRef();
    this.state = {
      allowEdit: props.allowEdit !== undefined ? props.allowEdit : true,
      speech: { identifier: null, state: false },
      spin_loading: false,
      mother_pog: null,
      mother_weight: null,
      mother_blood_pressure: null,
      mother_pulse_rate: null,
      mother_fundal_height: null,
      mother_edema: null,
      mother_pallor: null,
      mother_cervix_exam: null,
      mother_preterm_labour: null,
      mother_remarks: null,
    };
  }

  componentDidMount() {
    // POG (weeks) is auto-calculated from LMP and is not editable, matching the single
    // screen (weeks = whole weeks elapsed since LMP).
    const pog = this.calculatePogWeeks(this.props.lmp);
    this.setState({ mother_pog: pog });
    setTimeout(() => { this.formRef?.current?.setFieldsValue({ mother_pog: pog }); }, 0);

    this.subscription = speechTextService.status().subscribe((data) => {
      if (this.state.speech.state && this.state.speech.identifier === "mother_remarks") {
        this.setState({ mother_remarks: data.text });
        this.formRef?.current?.setFieldsValue({ mother_remarks: data.text });
        const el = document.querySelector("#mother_remarks");
        if (el) el.style.height = el.scrollHeight + "px";
      }
    });
  }

  // Whole weeks elapsed since LMP (POG). Returns null when LMP is missing/invalid.
  calculatePogWeeks = (lmp) => {
    if (!lmp) return null;
    const lmpMoment = moment(lmp);
    if (!lmpMoment.isValid()) return null;
    const days = moment().diff(lmpMoment, "days");
    if (days < 0) return null;
    return Math.floor(days / 7);
  };

  componentWillUnmount() {
    this?.subscription?.unsubscribe();
  }

  buildWhereCondition = () => ({
    patient_id: parseInt(LS_SERVICE.get("PATIENT_REG_DATA").registrationid),
    reference_id: parseInt(LS_SERVICE.get("call_patient_qms_token_id")),
    reference_type: 0,
    hospital_id: parseInt(LS_SERVICE.get("FACILITY_ID")),
    enterprise_id: parseInt(LS_SERVICE.get("ENTERPRISE_ID")),
    source: "OP",
  });

  handleOnTextChange = async (event) => {
    const { name, value } = event.currentTarget;
    await this.setState({ [name]: value });
    this.formRef?.current?.setFieldsValue({ [name]: value });
  };

  autoHeight = (e) => {
    if (e) e.target.style.height = e.target.scrollHeight + "px";
  };

  initSpeech = (info) => {
    const initialText = this.state[info.identifier] != undefined ? this.state[info.identifier] : "";
    if (info.identifier === this.state.speech.identifier) {
      this.setState({ speech: { identifier: info.identifier, state: !this.state.speech.state, initialText } }, () => {
        speechService.init(this.state.speech);
      });
    } else {
      this.setState({ speech: { identifier: this.state.speech.identifier, state: false, initialText: "" } }, () => {
        speechService.init(this.state.speech);
        setTimeout(() => {
          this.setState({ speech: { identifier: info.identifier, state: true, initialText } }, () => {
            speechService.init(this.state.speech);
          });
        }, 1000);
      });
    }
  };

  onFinish = async () => {
    await this.setState({ spin_loading: true });
    const mother_parameter_data = {
      pog: this.state.mother_pog !== "" ? this.state.mother_pog : null,
      weight: this.state.mother_weight !== "" ? this.state.mother_weight : null,
      blood_pressure: this.state.mother_blood_pressure !== "" ? this.state.mother_blood_pressure : null,
      pulse_rate: this.state.mother_pulse_rate !== "" ? this.state.mother_pulse_rate : null,
      fundal_height: this.state.mother_fundal_height !== "" ? this.state.mother_fundal_height : null,
      edema: this.state.mother_edema !== "" ? this.state.mother_edema : null,
      pallor: this.state.mother_pallor !== "" ? this.state.mother_pallor : null,
      cervix_exam: this.state.mother_cervix_exam !== "" ? this.state.mother_cervix_exam : null,
      preterm_labour: this.state.mother_preterm_labour !== "" ? this.state.mother_preterm_labour : null,
      mother_remarks: this.state.mother_remarks,
      // force_new: always insert a new row so multiple parameter readings can be
      // recorded for the same reference id. The ANC card displays the latest one.
      force_new: true,
      // Track who saved the parameters (doctor name with title).
      saved_by_doctor_id: LS_SERVICE.get("staff_id"),
      saved_by_doctor_name: LS_SERVICE.get("profile")?.data?.full_name || ("Dr. " + LS_SERVICE.get("staff_name")),
      created_by: LS_SERVICE.get("staff_id"),
      updated_by: LS_SERVICE.get("staff_id"),
    };

    const Params = { where_condition: this.buildWhereCondition(), mother_parameter_data };
    try {
      await Axios.post(Save_Mother_Parameter_Data, Params);
      notification.success({ message: "Mother parameters saved successfully", placement: "topRight" });
      await this.setState({ spin_loading: false });
      // Refresh the parent's data BEFORE closing so the table reflects the new record.
      if (this.props.onSaved) {
        await this.props.onSaved();
      }
      if (this.props.onClose) this.props.onClose();
    } catch (err) {
      notification.error({ message: "Something went wrong! Unable to save the data", placement: "topRight" });
      await this.setState({ spin_loading: false });
    }
  };

  render() {
    const { visible, onClose } = this.props;
    const { allowEdit } = this.state;
    return (
      <Modal
        title="Mother Parameters"
        visible={visible}
        onCancel={onClose}
        width="1100px"
        className="emrfrm"
        footer={false}
        destroyOnClose={true}
      >
        <Form layout="vertical" className="emrfrm" ref={this.formRef} onFinish={this.onFinish}>
          <div className="row">
            <div className="col-4">
              <div className="antInputItem hasdata">
                <Form.Item label="POG (Weeks)" name="mother_pog">
                  {/* Auto-calculated from LMP; not editable. */}
                  <Input id="mother_pog" name="mother_pog" readOnly disabled value={this.state.mother_pog} />
                </Form.Item>
              </div>
            </div>
            <div className="col-4">
              <div className="antInputItem hasdata">
                <Form.Item label="Weight (Kg)" name="mother_weight">
                  <Input id="mother_weight" name="mother_weight" disabled={!allowEdit} onChange={this.handleOnTextChange} value={this.state.mother_weight} />
                </Form.Item>
              </div>
            </div>
            <div className="col-4">
              <div className="antInputItem hasdata">
                <Form.Item label="BP (mmHg)" name="mother_blood_pressure">
                  <Input id="mother_blood_pressure" name="mother_blood_pressure" disabled={!allowEdit} onChange={this.handleOnTextChange} value={this.state.mother_blood_pressure} />
                </Form.Item>
              </div>
            </div>
          </div>
          <div className="row mt-2">
            <div className="col-4">
              <div className="antInputItem hasdata">
                <Form.Item label="Pulse Rate (min)" name="mother_pulse_rate">
                  <Input id="mother_pulse_rate" name="mother_pulse_rate" disabled={!allowEdit} onChange={this.handleOnTextChange} value={this.state.mother_pulse_rate} />
                </Form.Item>
              </div>
            </div>
            <div className="col-4">
              <div className="antInputItem hasdata">
                <Form.Item label="Fundal Ht (cm)" name="mother_fundal_height">
                  <Input id="mother_fundal_height" name="mother_fundal_height" disabled={!allowEdit} onChange={this.handleOnTextChange} value={this.state.mother_fundal_height} />
                </Form.Item>
              </div>
            </div>
            <div className="col-4">
              <div className="antInputItem hasdata">
                <Form.Item label="Edema" name="mother_edema">
                  <Input id="mother_edema" name="mother_edema" disabled={!allowEdit} onChange={this.handleOnTextChange} value={this.state.mother_edema} />
                </Form.Item>
              </div>
            </div>
          </div>
          <div className="row mt-2">
            <div className="col-4">
              <div className="antInputItem hasdata">
                <Form.Item label="Pallor" name="mother_pallor">
                  <Input id="mother_pallor" name="mother_pallor" disabled={!allowEdit} onChange={this.handleOnTextChange} value={this.state.mother_pallor} />
                </Form.Item>
              </div>
            </div>
            <div className="col-4">
              <div className="antInputItem hasdata">
                <Form.Item label="Cervix Exam" name="mother_cervix_exam">
                  <Input id="mother_cervix_exam" name="mother_cervix_exam" disabled={!allowEdit} onChange={this.handleOnTextChange} value={this.state.mother_cervix_exam} />
                </Form.Item>
              </div>
            </div>
            <div className="col-4">
              <div className="antInputItem hasdata">
                <Form.Item label="Preterm Labour (Sign/Symptoms)" name="mother_preterm_labour">
                  <Input id="mother_preterm_labour" name="mother_preterm_labour" disabled={!allowEdit} onChange={this.handleOnTextChange} value={this.state.mother_preterm_labour} />
                </Form.Item>
              </div>
            </div>
          </div>
          <div className="row mt-2">
            <div className="col-12">
              <div className="form-group antInputItem micwrap hasdata">
                <Link
                  onClick={(e) => { e.preventDefault(); this.initSpeech({ identifier: "mother_remarks" }); }}
                  className={this.state.speech.identifier === "mother_remarks" && this.state.speech.state ? "mic subjective-mic on mute" : "mic subjective-mic"}
                ></Link>
                <Form.Item name="mother_remarks" label="Remarks">
                  <Input.TextArea
                    className="form-control diagnosis_notes_class speech-field"
                    id="mother_remarks"
                    name="mother_remarks"
                    autoSize={{ minRows: 3, maxRows: 6 }}
                    disabled={!allowEdit}
                    onInput={(e) => this.autoHeight(e)}
                    onChange={this.handleOnTextChange}
                    value={this.state.mother_remarks}
                  />
                </Form.Item>
              </div>
            </div>
          </div>
          <div className="row mt-4 mb-2">
            <div className="col-12 text-center">
              <Form.Item>
                <button htmlType="submit" className="btn btn-primary px-5" disabled={this.state.spin_loading || !allowEdit}>
                  SAVE {this.state.spin_loading ? LOADER : null}
                </button>
              </Form.Item>
            </div>
          </div>
        </Form>
      </Modal>
    );
  }
}

export default MotherParameterModal;
